import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Box,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Slider,
  IconButton,
  InputAdornment,
  Button,
  CardActionArea,
  CircularProgress,
  Pagination,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { baseURL } from '../../utils/axios';
import {
  locationMap,
  watchMaterialMap,
  jewelryMaterialMap,
  braceletMap,
  conditionMap,
  statusMap,
  categoryMap,
  collectionMap,
  translateValue
} from '../../utils/productMappings';

const PRODUCT_CATEGORIES = {
  watch: 'Zegarki',
  engagement_ring: 'Pierścionki zaręczynowe',
  ring: 'Pierścionki',
  band: 'Obrączki',
  bracelet: 'Bransoletki',
  necklace: 'Naszyjniki',
  earrings: 'Kolczyki',
  brooch: 'Broszki',
  pendant: 'Zawieszki'
};

const WATCH_BRANDS = {
  "Rolex": [
    "Oyster Perpetual",
    "Datejust",
    "Day-Date",
    "Submariner",
    "GMT-Master II",
    "Explorer",
    "Yacht-Master"
  ],
  "Chopard": [
    "Happy Sport",
    "Mille Miglia",
    "L.U.C",
    "Alpine Eagle"
  ],
  "Cartier": [
    "Tank Must",
    "Tank Française",
    "Santos de Cartier",
    "Ballon Bleu",
    "Pasha de Cartier"
  ],
  "Audemars Piguet": [
    "Royal Oak",
    "Royal Oak Offshore",
    "Code 11.59"
  ],
  "Patek Philippe": [
    "Nautilus",
    "Aquanaut",
    "Calatrava",
    "Gondolo"
  ]
};

const WATCH_DIAMETERS = [
  24, 26, 28, 31, 34, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46
];

const WATCH_CONDITIONS = {
  unworn: 'Nie noszony',
  very_good: 'Bardzo dobry',
  good: 'Dobry'
};

const GOLD_COLORS = {
  yg: 'Żółte złoto',
  wg: 'Białe złoto',
  rg: 'Różowe złoto'
};

const MATERIALS = {
  steel: 'Stal',
  steel_yg: 'Stal + Żółte złoto',
  steel_rg: 'Stal + Różowe złoto',
  steel_wg: 'Stal + Białe złoto',
  yg: 'Żółte złoto',
  wg: 'Białe złoto',
  rg: 'Różowe złoto'
};

const JEWELRY_COLLECTIONS = {
  mm4men: 'MM4MEN',
  invisible: 'Invisible',
  mystery: 'Mystery',
  high_jewelers: 'High Jewelers'
};

const LOCATIONS = {
  PAN: 'Panorama M&M GOLD',
  VVS: 'Panorama VVS',
  MODO: 'Modo M&M GOLD',
  KLIF: 'Klif M&M GOLD',
  GP: 'Galeria Północna',
  SB: 'Stary Browar',
  MB: 'Marbella'
};

const STONE_TYPES = {
  diamond: 'Diament',
  ruby: 'Rubin',
  sapphire: 'Szafir',
  emerald: 'Szmaragd',
  tanzanite: 'Tanzanit',
  amethyst: 'Ametyst',
  topaz: 'Topaz',
  aquamarine: 'Akwamaryn',
  pearl: 'Perła',
  other: 'Inny'
};

const STONE_CUTS = {
  round: 'Okrągły',
  princess: 'Princess',
  cushion: 'Poduszka',
  emerald: 'Szmaragdowy',
  oval: 'Owalny',
  radiant: 'Promienisty',
  pear: 'Gruszka',
  marquise: 'Markiza',
  heart: 'Serce',
  asscher: 'Asscher',
  other: 'Inny'
};

const STONE_CLARITY = {
  FL: 'FL',
  IF: 'IF',
  VVS: 'VVS',
  VVS1: 'VVS1',
  VVS2: 'VVS2',
  VS: 'VS',
  VS1: 'VS1',
  VS2: 'VS2',
  SI: 'SI',
  SI1: 'SI1',
  SI2: 'SI2',
  I1: 'I1',
  I2: 'I2',
  I3: 'I3'
};

const CERTIFICATE_INSTITUTES = {
  gia: 'GIA',
  igi: 'IGI',
  hrd: 'HRD',
  other: 'INNY'
};

const ProductsList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [productsInTransfer, setProductsInTransfer] = useState({});
  const [lastVisible, setLastVisible] = useState(null);
  const loadingRef = useRef(false);
  const [productsInWorkshop, setProductsInWorkshop] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    sku: '',
    product_name: '',
    category: '',
    brand: '',
    model: '',
    location: '',
    price_min: 0,
    price_max: 10000000,  // Zwiększamy maksymalną cenę do 10M PLN
    custom_price_min: '',
    custom_price_max: '',
    case_diameter: '',
    condition: '',
    material: '',
    collection: '',
    jewelry_name: '',
    center_stone_type: '',
    center_stone_cut: '',
    center_stone_size_min: 0,
    center_stone_size_max: 10,
    center_stone_clarity: '',
    certificate_institute: '',
    total_carat_weight_min: 0,
    total_carat_weight_max: 20
  });

  console.log('Początkowe filtry:', filters);

  // Funkcja sprawdzająca lokalne potwierdzenia transferów
  const checkLocalTransferConfirmations = useCallback(() => {
    try {
      const localConfirmations = JSON.parse(localStorage.getItem('transferConfirmations') || '[]');
      const confirmedProducts = {};
      
      // Zbierz wszystkie produkty, które zostały lokalnie potwierdzone
      localConfirmations.forEach(confirmation => {
        if (confirmation.productStatus && confirmation.productStatus.id) {
          confirmedProducts[confirmation.productStatus.id] = {
            newLocation: confirmation.productStatus.newLocation,
            confirmed: true
          };
        }
      });
      
      return confirmedProducts;
    } catch (error) {
      console.error('Błąd odczytu lokalnych potwierdzeń:', error);
      return {};
    }
  }, []);
  
  // Stan do przechowywania produktów z aktywnymi zleceniami w pracowni
  // Funkcja sprawdzająca, które produkty mają aktywne zlecenie w pracowni
  const checkProductsInWorkshop = useCallback(async () => {
    try {
      const workshopOrdersRef = collection(db, 'workshop_orders');
      // Pobieramy tylko aktywne zlecenia (nie ukończone i nie anulowane)
      const workshopQuery = query(
        workshopOrdersRef, 
        where('status', 'in', ['waiting', 'in_progress'])
      );
      
      const ordersSnapshot = await getDocs(workshopQuery);
      const activeWorkshopOrders = {};
      
      ordersSnapshot.forEach(doc => {
        const order = doc.data();
        if (order.product_id) {
          activeWorkshopOrders[order.product_id] = {
            status: order.status,
            employeeId: order.employee_id,
            employeeName: order.employee_name
          };
        }
      });
      
      console.log('Produkty w pracowni:', activeWorkshopOrders);
      setProductsInWorkshop(activeWorkshopOrders);
    } catch (error) {
      console.error('Błąd podczas pobierania zleceń z pracowni:', error);
    }
  }, []);
  
  // Funkcja sprawdzająca, które produkty są aktualnie w transferze
  const checkProductsInTransfer = useCallback(async () => {
    try {
      const transfersRef = collection(db, 'transfers');
      // Używamy tylko where bez orderBy, aby uniknąć problemu z indeksem
      const transfersQuery = query(
        transfersRef, 
        where('status', '==', 'pending')
      );
      
      const transfersSnapshot = await getDocs(transfersQuery);
      const activeTransfers = {};
      
      transfersSnapshot.forEach(doc => {
        const transfer = doc.data();
        if (transfer.product_id && transfer.destination_location) {
          activeTransfers[transfer.product_id] = transfer.destination_location;
        }
      });
      
      // Sprawdź lokalne potwierdzenia i usuń produkty, które zostały już potwierdzone
      const confirmedProducts = checkLocalTransferConfirmations();
      
      // Usuń z aktywnych transferów produkty, które zostały lokalnie potwierdzone
      Object.keys(confirmedProducts).forEach(productId => {
        if (activeTransfers[productId]) {
          delete activeTransfers[productId];
        }
      });
      
      console.log('Produkty w transferze (po uwzględnieniu lokalnych potwierdzeń):', activeTransfers);
      setProductsInTransfer(activeTransfers);
    } catch (error) {
      console.error('Błąd podczas pobierania transferów:', error);
    }
  }, [checkLocalTransferConfirmations]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(price);
  };

  const fetchProducts = useCallback(async (currentPage = 0, currentFilters = filters, resetList = false) => {
    try {
      setLoading(true);
      
      // Jeśli resetujemy listę, czyszczmy lastVisible
      if (resetList) {
        setLastVisible(null);
      }
      
      const productsRef = collection(db, 'products');
      let q;

      // Jeśli to pierwsza strona lub resetujemy dane, to nie używamy startAfter
      if (currentPage === 0 || resetList) {
        // Budujemy podstawowe zapytanie z limitem 10 produktów
        q = query(productsRef, limit(10));
        setLastVisible(null);
      } else if (lastVisible) {
        // Dla kolejnych stron używamy startAfter z ostatnim widocznym dokumentem
        q = query(productsRef, startAfter(lastVisible), limit(10));
      } else {
        // Jeśli nie mamy lastVisible a chcemy pobrać kolejną stronę, to coś jest nie tak
        console.error('Próba pobrania następnej strony bez lastVisible');
        return;
      }

      // Filtrowanie po kategorii
      if (currentFilters.category) {
        q = query(q, where('category', '==', currentFilters.category));
      }

      // Filtrowanie po marce
      if (currentFilters.brand) {
        q = query(q, where('brand', '==', currentFilters.brand));
      }

      // Filtrowanie po modelu
      if (currentFilters.model) {
        q = query(q, where('model', '==', currentFilters.model));
      }

      // Filtrowanie po średnicy zegarka
      if (currentFilters.case_diameter) {
        q = query(q, where('case_diameter', '==', parseInt(currentFilters.case_diameter)));
      }

      // Filtrowanie po lokalizacji
      if (currentFilters.location) {
        q = query(q, where('location', '==', currentFilters.location));
      }

      console.log('Wykonywanie zapytania do Firestore z paginacją...');
      const querySnapshot = await getDocs(q);
      console.log('Otrzymano odpowiedź z Firestore');

      if (querySnapshot.empty) {
        setHasMore(false);
        if (currentPage === 0) {
          setProducts([]);
        }
        return;
      }

      // Zapisz ostatni widoczny dokument do paginacji
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(lastDoc);

      const productsData = [];
      querySnapshot.forEach((doc) => {
        productsData.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`Pobrano ${productsData.length} produktów z Firestore`);

      // Filtrowanie po cenie
      const filteredProducts = productsData.filter(product => {
        const price = parseFloat(product.price || 0);
        return price >= currentFilters.price_min && price <= currentFilters.price_max;
      });

      // Filtrowanie po wyszukiwaniu ogólnym
      const searchTerm = currentFilters.search.toLowerCase();
      let searchResults = searchTerm
        ? filteredProducts.filter(product => {
            const productString = Object.values(product)
              .filter(val => typeof val === 'string' || typeof val === 'number')
              .map(val => String(val).toLowerCase())
              .join(' ');
            return productString.includes(searchTerm);
          })
        : filteredProducts;
        
      // Filtrowanie po nazwie produktu
      if (currentFilters.product_name) {
        const productNameTerm = currentFilters.product_name.toLowerCase();
        searchResults = searchResults.filter(product => {
          const productNameString = [
            product.product_name,
            product.model,
            product.brand,
            product.jewelry_name,
            product.name
          ]
            .filter(val => val)
            .map(val => String(val).toLowerCase())
            .join(' ');
          return productNameString.includes(productNameTerm);
        });
      }
      
      // Pozostałe filtry...
      // [reszta kodu filtrowania bez zmian]

      console.log('Końcowe wyniki po filtrowaniu:', searchResults);

      // Jeśli to resetowanie danych lub pierwsza strona, zastąp produkty
      if (resetList || currentPage === 0) {
        setProducts(searchResults);
      } else {
        // W przeciwnym razie dodaj nowe produkty do istniejących
        setProducts(prevProducts => [...prevProducts, ...searchResults]);
      }

      // Sprawdź czy są kolejne strony
      setHasMore(querySnapshot.docs.length === 10);
      
      // Zaktualizuj numer strony
      setPage(currentPage);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, lastVisible]);

  useEffect(() => {
    const fetchInitialProducts = async () => {
      setLoading(true);
      await fetchProducts(0, filters, true);
      setLoading(false);
    };

    fetchInitialProducts();
    checkProductsInWorkshop();
    
    // Ustaw flagi transferów na podstawie lokalnych potwierdzeń
    setProductsInTransfer(checkLocalTransferConfirmations());
    
    // Funkcja nasłuchująca na scrollowanie
    const handleScroll = () => {
      if (loadingRef.current || !hasMore) return;
      
      // Obliczamy czy jesteśmy blisko dołu strony
      const scrollHeight = document.documentElement.scrollHeight;
      const currentHeight = window.innerHeight + window.scrollY;
      
      // Jeśli jesteśmy wystarczająco blisko dołu strony, ładujemy więcej produktów
      if (currentHeight + 100 >= scrollHeight) {
        loadingRef.current = true;
        setPage(prevPage => prevPage + 1);
        fetchProducts(page + 1);
      }
    };
    
    // Dodaj nasłuchiwanie na scroll (WYŁĄCZONE, żeby nie odświeżać automatycznie)
    // window.addEventListener('scroll', handleScroll);
    
    // Usunięcie nasłuchiwania przy odmontowaniu komponentu
    return () => {
      // window.removeEventListener('scroll', handleScroll);
    };
  }, [checkProductsInWorkshop, checkLocalTransferConfirmations, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };

      if (field === 'priceRange') {
        newFilters.price_min = value[0];
        newFilters.price_max = value[1];
        return newFilters;
      }

      if (field === 'centerStoneSize') {
        newFilters.center_stone_size_min = value[0];
        newFilters.center_stone_size_max = value[1];
        return newFilters;
      }

      if (field === 'totalCarats') {
        newFilters.total_carat_weight_min = value[0];
        newFilters.total_carat_weight_max = value[1];
        return newFilters;
      }

      const fieldMapping = {
        productType: 'category',
        watchBrand: 'brand',
        watchModel: 'model',
        watchDiameter: 'case_diameter',
        jewelryCollection: 'collection'
      };

      const mappedField = fieldMapping[field] || field;
      newFilters[mappedField] = value;

      if (field === 'category') {
        newFilters.brand = '';
        newFilters.model = '';
        newFilters.collection = '';
      } else if (field === 'brand') {
        newFilters.model = '';
      }

      return newFilters;
    });
  };

  const handleSearch = () => {
    setPage(0);
    setProducts([]);
    fetchProducts(0, filters);
  };

  const clearFilters = () => {
    const defaultFilters = {
      search: '',
      category: '',
      brand: '',
      model: '',
      location: '',
      price_min: 0,
      price_max: 10000000,
      custom_price_min: '',
      custom_price_max: '',
      case_diameter: '',
      condition: '',
      material: '',
      collection: '',
      jewelry_name: '',
      center_stone_type: '',
      center_stone_cut: '',
      center_stone_size_min: 0,
      center_stone_size_max: 10,
      center_stone_clarity: '',
      certificate_institute: '',
      total_carat_weight_min: 0,
      total_carat_weight_max: 20
    };

    setFilters(defaultFilters);
    setPage(0);
    fetchProducts(0, defaultFilters);
  };

  const renderProductCard = (product) => {
    const renderProductDetails = () => {
      if (product.category === 'watch') {
        return (
          <>
            <Typography variant="body2" color="text.secondary" noWrap>
              {product.brand}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {product.model}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Materiał: {MATERIALS[product.material] || product.material}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Stan: {WATCH_CONDITIONS[product.condition] || product.condition}
            </Typography>
            {product.case_diameter && (
              <Typography variant="body2" color="text.secondary" noWrap>
                Średnica: {product.case_diameter} mm
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" noWrap>
              Rok: {product.year || product.production_year}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Stan: {product.condition === 'new' ? 'Nowy' : 'Używany'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Kolor tarczy: {product.dial_color}
            </Typography>
          </>
        );
      } else if (product.category === 'jewelry' || product.jewelry_type) {
        return (
          <>
            <Typography variant="body2" color="text.secondary" noWrap>
              {product.jewelry_name || PRODUCT_CATEGORIES[product.jewelry_type] || PRODUCT_CATEGORIES[product.category]}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Materiał: {MATERIALS[product.material] || product.material}
            </Typography>
            {product.gold_purity && (
              <Typography variant="body2" color="text.secondary" noWrap>
                Próba: {product.gold_purity}
              </Typography>
            )}
            {product.weight_grams && (
              <Typography variant="body2" color="text.secondary" noWrap>
                Waga: {product.weight_grams}g
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" noWrap>
              SKU: {product.sku || 'N/A'}
            </Typography>
            {product.side_stones_count > 0 && (
              <Typography variant="body2" color="text.secondary" noWrap>
                Kamienie: {product.side_stones_count}x {STONE_TYPES[product.side_stones_type] || product.side_stones_type}
              </Typography>
            )}
            {product.side_stones_weight > 0 && (
              <Typography variant="body2" color="text.secondary" noWrap>
                Waga kamieni: {product.side_stones_weight}ct
              </Typography>
            )}
            {product.center_stone_type && (
              <Typography variant="body2" color="text.secondary" noWrap>
                Kamień: {STONE_TYPES[product.center_stone_type] || product.center_stone_type}
              </Typography>
            )}
            {product.center_stone_size && (
              <Typography variant="body2" color="text.secondary" noWrap>
                Wielkość: {product.center_stone_size}ct
              </Typography>
            )}
          </>
        );
      } else {
        return (
          <Typography variant="body2" color="text.secondary" noWrap>
            {product.description}
          </Typography>
        );
      }
    };

    return (
      <Card sx={{ 
        width: '100%',
        height: '100%', 
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}>
        <CardActionArea onClick={() => navigate(`/products/${product.id}`)} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
          {product.images && product.images.length > 0 ? (
            <CardMedia
              component="img"
              height="200"
              image={Array.isArray(product.images) ? product.images[0] : product.images}
              alt={product.product_name || product.brand}
              sx={{ 
                objectFit: 'contain', 
                backgroundColor: '#0A0A0A',
                background: 'linear-gradient(145deg, #0A0A0A 0%, #151515 100%)'
              }}
            />
          ) : (
            <Box sx={{ 
              height: 200, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              backgroundColor: '#0A0A0A',
              background: 'linear-gradient(145deg, #0A0A0A 0%, #151515 100%)'
            }}>
              <Typography variant="body2" color="text.secondary">
                Brak zdjęcia
              </Typography>
            </Box>
          )}
          <CardContent sx={{ flexGrow: 1, width: '100%', p: 2 }}>
            <Typography variant="h6" component="div" noWrap sx={{ fontWeight: 'bold' }}>
              {product.category === 'watch' 
                ? `${product.brand} ${product.model}` 
                : product.jewelry_name || product.product_name}
            </Typography>
            {renderProductDetails()}
            <Typography variant="h6" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
              {formatPrice(product.price)}
            </Typography>
            {(product.status === 'in_workshop' || (productsInWorkshop && productsInWorkshop[product.id])) && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 10, 
                  right: 10, 
                  bgcolor: 'warning.main', 
                  color: 'white', 
                  px: 1, 
                  py: 0.5, 
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}
              >
                W PRACOWNI
              </Box>
            )}
            {product.status === 'sale_reported' && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 10, 
                  right: 10, 
                  bgcolor: 'info.main', 
                  color: 'white', 
                  px: 1, 
                  py: 0.5, 
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}
              >
                ZGŁOSZONA SPRZEDAŻ
              </Box>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const renderFilters = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Kategoria</InputLabel>
            <Select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              label="Kategoria"
            >
              <MenuItem value="">Wszystkie kategorie</MenuItem>
              {Object.entries(PRODUCT_CATEGORIES).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {filters.category === 'watch' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Marka</InputLabel>
              <Select
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                label="Marka"
              >
                <MenuItem value="">Wszystkie marki</MenuItem>
                {Object.keys(WATCH_BRANDS).map((brand) => (
                  <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filters.category === 'watch' && filters.brand && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Model</InputLabel>
              <Select
                value={filters.model}
                onChange={(e) => handleFilterChange('model', e.target.value)}
                label="Model"
              >
                <MenuItem value="">Model</MenuItem>
                {WATCH_BRANDS[filters.brand].map(model => (
                  <MenuItem key={model} value={model}>{model}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filters.category === 'watch' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Średnica</InputLabel>
              <Select
                value={filters.case_diameter}
                onChange={(e) => handleFilterChange('case_diameter', e.target.value)}
                label="Średnica"
              >
                <MenuItem value="">Średnica</MenuItem>
                {WATCH_DIAMETERS.map(diameter => (
                  <MenuItem key={diameter} value={diameter}>{diameter} mm</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filters.category === 'watch' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Stan</InputLabel>
              <Select
                value={filters.condition}
                onChange={(e) => handleFilterChange('condition', e.target.value)}
                label="Stan"
              >
                <MenuItem value="">Stan</MenuItem>
                {Object.entries(WATCH_CONDITIONS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filters.category === 'jewelry' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Kolekcja</InputLabel>
              <Select
                value={filters.collection}
                onChange={(e) => handleFilterChange('collection', e.target.value)}
                label="Kolekcja"
              >
                <MenuItem value="">Kolekcja</MenuItem>
                {Object.entries(JEWELRY_COLLECTIONS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Materiał</InputLabel>
            <Select
              value={filters.material}
              onChange={(e) => handleFilterChange('material', e.target.value)}
              label="Materiał"
            >
              <MenuItem value="">Materiał</MenuItem>
              {Object.entries(MATERIALS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Lokalizacja</InputLabel>
            <Select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              label="Lokalizacja"
            >
              <MenuItem value="">Lokalizacja</MenuItem>
              {Object.entries(LOCATIONS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Wyszukaj"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            variant="outlined"
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
            placeholder="Wyszukaj ogólnie..."
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Nazwa produktu"
            value={filters.product_name}
            onChange={(e) => handleFilterChange('product_name', e.target.value)}
            variant="outlined"
            placeholder="Wyszukaj po nazwie produktu..."
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="ID"
            value={filters.sku}
            onChange={(e) => handleFilterChange('sku', e.target.value)}
            variant="outlined"
            placeholder="Wpisz ID..."
          />
        </Grid>

        {filters.category === 'jewelry' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Rodzaj kamienia centralnego</InputLabel>
              <Select
                value={filters.center_stone_type}
                onChange={(e) => handleFilterChange('center_stone_type', e.target.value)}
                label="Rodzaj kamienia centralnego"
              >
                <MenuItem value="">Wszystkie rodzaje</MenuItem>
                {Object.entries(STONE_TYPES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filters.category === 'jewelry' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Szlif kamienia centralnego</InputLabel>
              <Select
                value={filters.center_stone_cut}
                onChange={(e) => handleFilterChange('center_stone_cut', e.target.value)}
                label="Szlif kamienia centralnego"
              >
                <MenuItem value="">Wszystkie szlify</MenuItem>
                {Object.entries(STONE_CUTS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filters.category === 'jewelry' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Czystość kamienia centralnego</InputLabel>
              <Select
                value={filters.center_stone_clarity}
                onChange={(e) => handleFilterChange('center_stone_clarity', e.target.value)}
                label="Czystość kamienia centralnego"
              >
                <MenuItem value="">Wszystkie czystości</MenuItem>
                {Object.entries(STONE_CLARITY).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filters.category === 'jewelry' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Certyfikacja</InputLabel>
              <Select
                value={filters.certificate_institute}
                onChange={(e) => handleFilterChange('certificate_institute', e.target.value)}
                label="Certyfikacja"
              >
                <MenuItem value="">Wszystkie certyfikaty</MenuItem>
                {Object.entries(CERTIFICATE_INSTITUTES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} md={8}>
          <Typography gutterBottom>
            Zakres cenowy: {formatPrice(filters.price_min)} - {formatPrice(filters.price_max)}
          </Typography>
          <Slider
            value={[filters.price_min, filters.price_max]}
            onChange={(_, newValue) => handleFilterChange('priceRange', newValue)}
            valueLabelDisplay="auto"
            min={0}
            max={10000000}
            step={100000}
            valueLabelFormat={formatPrice}
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Własna cena min. (PLN)"
            value={filters.custom_price_min}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              handleFilterChange('custom_price_min', value);
              if (value) {
                handleFilterChange('price_min', parseInt(value, 10));
              }
            }}
            variant="outlined"
            type="text"
            InputProps={{
              endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
            }}
            placeholder="Wpisz własną wartość minimalną"
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Własna cena max. (PLN)"
            value={filters.custom_price_max}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              handleFilterChange('custom_price_max', value);
              if (value) {
                handleFilterChange('price_max', parseInt(value, 10));
              }
            }}
            variant="outlined"
            type="text"
            InputProps={{
              endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
            }}
            placeholder="Wpisz własną wartość maksymalną"
          />
        </Grid>

        {filters.category === 'jewelry' && (
          <Grid item xs={12} md={8}>
            <Typography gutterBottom>
              Wielkość kamienia centralnego: {filters.center_stone_size_min} - {filters.center_stone_size_max} ct
            </Typography>
            <Slider
              value={[filters.center_stone_size_min, filters.center_stone_size_max]}
              onChange={(_, newValue) => handleFilterChange('centerStoneSize', newValue)}
              valueLabelDisplay="auto"
              min={0}
              max={10}
              step={0.1}
              valueLabelFormat={(value) => `${value} ct`}
            />
          </Grid>
        )}

        {filters.category === 'jewelry' && (
          <Grid item xs={12} md={8}>
            <Typography gutterBottom>
              Łączna liczba karatów: {filters.total_carat_weight_min} - {filters.total_carat_weight_max} ct
            </Typography>
            <Slider
              value={[filters.total_carat_weight_min, filters.total_carat_weight_max]}
              onChange={(_, newValue) => handleFilterChange('totalCarats', newValue)}
              valueLabelDisplay="auto"
              min={0}
              max={20}
              step={0.1}
              valueLabelFormat={(value) => `${value} ct`}
            />
          </Grid>
        )}

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              disabled={loading}
            >
              Wyszukaj
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined"
              onClick={clearFilters}
              startIcon={<FilterListIcon />}
              disabled={loading}
            >
              Wyczyść filtry
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderProducts = () => {
    if (loading && products.length === 0) {
      return (
        <Box display="flex" justifyContent="center" my={4} width="100%">
          <CircularProgress />
        </Box>
      );
    }

    if (products.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', my: 4, width: "100%" }}>
          <Typography variant="h6" color="text.secondary">
            Brak produktów spełniających kryteria wyszukiwania
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3} sx={{ width: '100%', margin: '0' }}>
        {products.map(product => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <Box sx={{ height: '100%' }}>
              {renderProductCard(product)}
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box>
      <Accordion sx={{ mb: 2 }} defaultExpanded={true}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="filter-panel-content"
          id="filter-panel-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterListIcon sx={{ mr: 1 }} />
            <Typography>Filtry wyszukiwania</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {renderFilters()}
        </AccordionDetails>
      </Accordion>

      {/* Produkty na całą szerokość */}
      {renderProducts()}
      
      {/* Paginacja na dole */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            {hasMore && (
              <Button 
                variant="outlined" 
                onClick={() => fetchProducts(page + 1)}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                Pokaż więcej produktów
              </Button>
            )}
            <Typography variant="body2" color="text.secondary">
              Strona {page + 1}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ProductsList;